import { useEffect, useState, useContext } from "react";
import axios from "axios";
import DynamicTable from "../../../Components/DynamicTable";
import AddBrand from "./AddBrand";
import UserContext from "../../../UserContext";
import { Box, Typography, Button, Stack, Paper } from "@mui/material";
import Avatar from "../../../Components/Avatar";
import SlidingSidebar, {
  ButtonControls,
  SidebarAvatar,
  SidebarEditForm,
  SwitchControl,
} from "../../../Components/SlidingSidebar";
import {
  CheckCircle as CheckCircleIcon,
  Cancel as XIcon,
  WatchLater as WatchLaterIcon,
} from "@mui/icons-material";

interface Brand {
  brand_id: number;
  brand: string;
  avatar_url: string;
  supplier_id: number;
  supplier: string;
  username: string;
  brand_status: {
    Verified: "Yes" | "No";
    Active: "Yes" | "No";
  };
}

const Brands = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { selectedSupplierId } = useContext(UserContext) || {};

  const handleAvatarUpload = async (file: File, brandId: number) => {
    const formData = new FormData();
    formData.append("avatar", file);

    try {
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/photos/update-avatar/brand/${brandId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Update both the brands list and the editing brand with new avatar URL
      const newAvatarUrl = response.data.avatar_url;

      setBrands(
        brands.map((brand) =>
          brand.brand_id === brandId
            ? { ...brand, avatar_url: newAvatarUrl }
            : brand
        )
      );

      if (editingBrand && editingBrand.brand_id === brandId) {
        setEditingBrand({
          ...editingBrand,
          avatar_url: newAvatarUrl,
        });
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
    }
  };

  const handleToggleActive = async (brandId: number, currentStatus: string) => {
    try {
      const formData = new FormData();
      formData.append(
        "brand_status",
        JSON.stringify({
          Active: currentStatus === "Yes" ? "No" : "Yes",
        })
      );

      await axios.put(
        `${import.meta.env.VITE_API_URL}/brands/brand/${brandId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      fetchBrands();
    } catch (error) {
      console.error("Error updating brand status:", error);
    }
  };

  const brandColumns = [
    {
      header: "Avatar",
      dataIndex: "avatar_url",
      key: "avatar",
      render: (_: string, record: Brand) => (
        <Avatar
          src={record.avatar_url || "/default-avatar.png"}
          alt={`Avatar of ${record.brand}`}
          size={40}
        />
      ),
    },
    { header: "Brand Name", dataIndex: "brand", key: "brand" },
    { header: "Username", dataIndex: "username", key: "username" },
    {
      header: "Verified",
      dataIndex: ["brand_status.verified", "Verified"],
      key: "brand_status.verified",
      render: (verified: "Yes" | "No", record: Brand) => (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {record.brand_status.Verified === "Yes" ? (
              <CheckCircleIcon sx={{ color: "primary.main" }} />
            ) : (
              <WatchLaterIcon sx={{ color: "primary.main" }} />
            )}
          </Box>
          {verified === "No" && (
            <Typography
              variant="body2"
              sx={{ fontStyle: "italic", mt: 1, color: "text.secondary" }}
            >
              This brand is pending validation from PROMORAVEN. You will be
              notified once it has been validated.
            </Typography>
          )}
        </Box>
      ),
    },
    {
      header: "Active",
      dataIndex: ["brand_status.active", "Active"],
      key: "brand_status.active",
      align: "center" as const,
      render: (_: "Yes" | "No", record: Brand) => (
        <Box sx={{ textAlign: "center" }}>
          {record.brand_status.Active === "Yes" ? (
            <CheckCircleIcon
              sx={{ color: "primary.main", cursor: "pointer" }}
              onClick={() => handleToggleActive(record.brand_id, "Yes")}
            />
          ) : (
            <XIcon
              sx={{ color: "text.disabled", cursor: "pointer" }}
              onClick={() => handleToggleActive(record.brand_id, "No")}
            />
          )}
        </Box>
      ),
    },
  ];

  const fetchBrands = async () => {
    if (!selectedSupplierId) return;

    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/brands/brand-list`,
        { params: { supplier_id: selectedSupplierId } }
      );
      setBrands(response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, [selectedSupplierId]);

  const handleEdit = (brand: Brand) => {
    setEditingBrand(brand);
    setSidebarOpen(true);
  };

  const handleSave = async () => {
    if (!editingBrand) return;

    try {
      const formData = new FormData();

      // Only include changed fields
      if (
        editingBrand.brand !==
        brands.find((b) => b.brand_id === editingBrand.brand_id)?.brand
      ) {
        formData.append("brand", editingBrand.brand);
      }

      if (
        editingBrand.username !==
        brands.find((b) => b.brand_id === editingBrand.brand_id)?.username
      ) {
        formData.append("username", editingBrand.username);
      }

      // Include brand status if it exists
      if (editingBrand.brand_status) {
        formData.append(
          "brand_status",
          JSON.stringify(editingBrand.brand_status)
        );
      }

      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/brands/brand/${editingBrand.brand_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Update the brands list with the new data
      setBrands(
        brands.map((brand) =>
          brand.brand_id === editingBrand.brand_id ? response.data : brand
        )
      );

      setSidebarOpen(false);
      setEditingBrand(null);
    } catch (error) {
      console.error("Error updating brand:", error);
    }
  };
  console.log("brands", brands);
  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <AddBrand onBrandAdded={fetchBrands} />
      </Box>
      <DynamicTable
        data={brands}
        columns={brandColumns}
        onRowClick={handleEdit}
      />

      <SlidingSidebar
        open={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          setEditingBrand(null);
        }}
        width="500px"
      >
        {editingBrand && (
          <Box
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ flexGrow: 1, overflow: "auto", pb: 2 }}>
              <SidebarAvatar
                src={editingBrand.avatar_url || "/default-avatar.png"}
                alt={editingBrand.brand}
                onEditAvatar={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/*";
                  input.onchange = (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) {
                      handleAvatarUpload(file, editingBrand.brand_id);
                    }
                  };
                  input.click();
                }}
              />

              <SidebarEditForm
                sections={[
                  {
                    title: "Brand Information",
                    fields: [
                      {
                        label: "Brand Name",
                        value: editingBrand.brand,
                        field: "brand",
                        required: true,
                      },
                      {
                        label: "Username",
                        value: editingBrand.username,
                        field: "username",
                        required: true,
                      },
                    ],
                  },
                ]}
                onValueChange={(field: string, value: any) =>
                  setEditingBrand({
                    ...editingBrand,
                    [field]: value,
                  })
                }
              />

              <Box sx={{ p: 2 }}>
                <Paper elevation={0} sx={{ p: 1 }}>
                  <Stack spacing={2}>
                    <Typography variant="h6">Permissions</Typography>
                    <SwitchControl
                      label="Verified"
                      checked={editingBrand.brand_status?.Verified === "Yes"}
                      onChange={() =>
                        setEditingBrand({
                          ...editingBrand,
                          brand_status: {
                            ...editingBrand.brand_status,
                            Verified:
                              editingBrand.brand_status?.Verified === "Yes"
                                ? "No"
                                : "Yes",
                          },
                        })
                      }
                    />
                    <SwitchControl
                      label="Active"
                      checked={editingBrand.brand_status?.Active === "Yes"}
                      onChange={() =>
                        setEditingBrand({
                          ...editingBrand,
                          brand_status: {
                            ...editingBrand.brand_status,
                            Active:
                              editingBrand.brand_status?.Active === "Yes"
                                ? "No"
                                : "Yes",
                          },
                        })
                      }
                    />
                  </Stack>
                </Paper>
              </Box>
            </Box>

            <ButtonControls>
              <Button onClick={handleSave} variant="contained" color="primary">
                Save Changes
              </Button>
              <Button
                onClick={() => {
                  setSidebarOpen(false);
                  setEditingBrand(null);
                }}
                variant="outlined"
              >
                Cancel
              </Button>
            </ButtonControls>
          </Box>
        )}
      </SlidingSidebar>
    </Box>
  );
};

export default Brands;
